import { isFunction, isUndefined, isArray } from 'lodash'

import { getIdOrNull, getMatrix, hasValidId, isObject, isOptimistic, sortById } from '../../../utils'
import { Quota } from '../../../utils/entities/quota'
import { Actions, Events } from '../../../utils/events'
import { localStorage } from '../../../utils/localStorage'
import { API_DATE, getEndOf, getStartOf, ISO_WEEK, MONTH_STRING_FORMAT, WEEK_NUMBER_FORMAT } from '../../../utils/momentjs'
import { PeriodTypes } from '../../../utils/timePeriods'
import { Types } from '../../antd/Tables/DragTable/ResizingDiv'
import { cellDisplacement, getXCoordinates, getYCoordinates, isEmptySubMatrix } from '../../antd/Tables/utils'
import { Properties } from '../../../utils/entities'

export const LaneTypes = Object.freeze({
  SECTION: 'section',
  GROUP: 'sectionGroup'
})

const KEY_SEPARATOR = '_'
export const INCLUSION_LINE_KEY = -1

export const getLaneType = (entity) => isObject(entity) && hasValidId(entity[LaneTypes.GROUP]) ? LaneTypes.GROUP : LaneTypes.SECTION
export const getLaneKey = (type, entity) => hasValidId(entity) ? `${type}${KEY_SEPARATOR}${getIdOrNull(entity)}` : -1
export const getKeyConfig = (laneKey) => {
  const [type, id] = laneKey.split(KEY_SEPARATOR)

  return { type, id }
}

export const getLaneConfig = (quota) => {
  const { SECTION, GROUP } = LaneTypes

  const type = getLaneType(quota)
  let name = ''
  let id = -1

  if (isObject(quota) && (hasValidId(quota[SECTION]) || hasValidId(quota[GROUP]))) {
    const label = type === SECTION ? Properties.ABBREVIATION : Properties.NAME
    const entity = quota[type]

    name = entity[label]
    id = getIdOrNull(entity)
  }

  return { type, id, name }
}

export const sortLanes = lanes => {
  const sortByType = (a, b) => {
    const { GROUP, SECTION } = LaneTypes

    if (a.type === GROUP && b.type === SECTION) {
      return -1
    } else if (a.type === SECTION && b.type === GROUP) {
      return 1
    } else {
      return 0
    }
  }

  const copy = [...lanes]

  return copy.sort(sortById).sort(sortByType)
}

export const getQuotasPeriods = ({ startDate, type, quantity }, translator) => {
  const periods = {
    months: [],
    weeks: []
  }
  const step = PeriodTypes.WEEK

  let iterationDate = startDate.clone()
  let currentMonth = {
    month: iterationDate.month(),
    label: translator(iterationDate.format(MONTH_STRING_FORMAT)),
    weeks: []
  }

  for (let iStep = 0; iStep < quantity; iStep++) {
    currentMonth.weeks.push({
      startDate: iterationDate.clone().startOf(step),
      endDate: iterationDate.clone().endOf(step),
      number: iterationDate.format(WEEK_NUMBER_FORMAT)
    })

    periods.weeks.push(iterationDate.clone())
    iterationDate = iterationDate.clone().add(1, step)

    if (iterationDate.month() !== currentMonth.month) {
      periods.months.push(currentMonth)

      currentMonth = {
        month: iterationDate.month(),
        label: translator(iterationDate.format(MONTH_STRING_FORMAT)),
        weeks: []
      }
    }
  }

  if (currentMonth.weeks.length > 0) {
    periods.months.push(currentMonth)
  }

  return periods
}

export const getQuotasMatrix = (lines, weeks, quotas, getConfig, getValue, storageKey) => {
  const quotasAcc = lines.reduce((acc, line) => {
    const places = line.id === INCLUSION_LINE_KEY && line.places === 0 ? 1 : line.places

    acc.data[line.id] = []
    acc.matrix[line.id] = getMatrix(weeks.length, places)
    acc.places[line.id] = line.places

    return acc
  }, { data: {}, matrix: {}, places: {} })

  const rawQuotas = [...quotas]
  const formattedQuotas = []
  let storedQuotas = {}

  if (storageKey && localStorage.has(storageKey)) {
    storedQuotas = localStorage.get(storageKey)
  }

  rawQuotas.forEach((q, index) => {
    const laneType = getLaneType(q)
    const laneKey = getLaneKey(laneType, q[laneType])

    const key = isUndefined(quotasAcc.matrix[laneKey]) ? INCLUSION_LINE_KEY : laneKey

    const x = getXCoordinates(q, weeks)

    let type = q.isDesiderata ? Types.CLEAR : Types.SOLID

    if (!isUndefined(getConfig(q).type)) {
      type = getConfig(q).type
    }

    formattedQuotas.push({
      id: q.id,
      key,
      alias: index + 1,
      x,
      value: getValue(q),
      config: getConfig(q),
      type,
      quota: q
    })
  })

  formattedQuotas.sort((a, b) => {
    const aLength = a.x[1] - a.x[0]
    const bLength = b.x[1] - b.x[0]

    if (storedQuotas[a.id] && !storedQuotas[b.id]) {
      return -1
    } else if (!storedQuotas[a.id] && storedQuotas[b.id]) {
      return 1
    } else if (aLength === bLength) {
      return a.quota.total > b.quota.total ? -1 : 1
    } else if (aLength > bLength) {
      return -1
    } else {
      return 1
    }
  })

  formattedQuotas.forEach(q => {
    const key = q.key
    const x = q.x

    const isOverflow = key === INCLUSION_LINE_KEY && quotasAcc.places[key] === 0
    const matrix = quotasAcc.matrix[key] && quotasAcc.matrix[key].length > 0 ? quotasAcc.matrix[key] : getMatrix(weeks.length, 1)
    let y

    if (storedQuotas[q.id]) {
      y = storedQuotas[q.id].y
    } else {
      y = getYCoordinates(q.quota, matrix, x)
    }

    if (isOverflow && y[0] === 0) {
      y = y.map(coord => ++coord)
    }

    /** @TODO improve algorithm to complete and fill in only one action */
    if (y[1] >= quotasAcc.matrix[key].length) {
      for (let i = quotasAcc.matrix[key].length; i <= y[1]; i++) {
        quotasAcc.matrix[key].push(new Array(weeks.length).fill(0))
      }
    }

    const width = x[1] >= weeks.length ? weeks.length - 1 : x[1]

    for (let i = y[0]; i <= y[1]; i++) {
      for (let j = x[0]; j <= width; j++) {
        quotasAcc.matrix[key][i][j] = q.alias
      }
    }

    q.y = y

    quotasAcc.data[key].push(q)
  })

  const overflows = lines.reduce((acc, line) => {
    const overflow = quotasAcc.matrix[line.id].length - Math.max(1, line.places)

    acc[line.id] = Math.max(overflow, 0)
    return acc
  }, {})

  return { data: quotasAcc.data, overflows, matrix: quotasAcc.matrix }
}

export const getQuotasMap = (quotas, buildQuota) => {
  return quotas.reduce((acc, quota) => {
    if (quota.school === null && !quota.isDesiderata) {
      acc.places.push(quota)
    } else if (quota.total === 0) {
      acc.quotasNull.push(quota)
    } else if (quota.isDesiderata) {
      if (quota.school !== null) {
        acc.desideratas.push(quota)
      }
    } else {
      if (isFunction(buildQuota)) {
        quota = buildQuota(quota)
      }

      acc.quotas.push(quota)
    }

    return acc
  }, new QuotasMap())
}

export const reduceBySector = data => {
  return data.reduce((acc, d) => {
    const key = d.sector.id

    if (!acc[key]) {
      acc[key] = []
    }

    acc[key].push(d)

    return acc
  }, {})
}

export const getDifference = (a, b) => {
  return a.filter(x => !b.some(y => x.id === y.id))
}

export const getDivStyle = ({ background, border, text }) => {
  return {
    backgroundColor: background,
    borderColor: border,
    color: text
  }
}

export const quotaEvent = (data, matrix, periods, event, rawData, seed, storageKey) => {
  let quota = null

  if (rawData !== null) {
    const coordinates = new Coordinates(rawData)
    const oldCoordinates = new Coordinates(data?.find(d => d.id === rawData.id) ?? { x: [-1, -1], y: [-1, -1] })
    quota = new Quota(rawData.quota ?? seed)
    const startDate = periods.weeks[0]

    quota.startDate = getStartOf(startDate, ISO_WEEK, rawData.x[0]).format(API_DATE)
    quota.endDate = getEndOf(startDate, ISO_WEEK, rawData.x[1]).format(API_DATE)
    quota.total = rawData.y[1] - rawData.y[0] + 1

    const spaceIsFree = isEmptySubMatrix(matrix, coordinates.y[0], coordinates.y[1], coordinates.x[0], coordinates.x[1], rawData.alias)

    if (!spaceIsFree && matrix.length > 0) {
      const newPositions = cellDisplacement(matrix, data ?? [], { ...coordinates, alias: rawData.alias })

      Object.keys(newPositions).forEach(alias => {
        const quotaData = data.find(d => d.alias === Number(alias))

        if (quotaData) {
          localStorage.add(storageKey, { key: quotaData.id, value: newPositions[alias] })
        }
      })
    }

    if (coordinates.isOnlyVerticalDisplacement(oldCoordinates)) {
      event = Events.STRICT_VERTICAL_DRAG_STOP
    }

    localStorage.add(storageKey, { key: quota.id, value: coordinates })
  }

  return { quota, event }
}

export const getSeed = (key, rawData) => {
  const isValidKey = !isUndefined(key) && key !== INCLUSION_LINE_KEY
  const isQuotaOptimistic = (!rawData?.quota || isOptimistic(rawData.quota))

  const entity = {}

  if (isValidKey && isQuotaOptimistic) {
    const { type, id } = getKeyConfig(key)

    entity[type] = { id }
  }

  return entity
}

export const updateQuotaByActions = (rawQuotas, params, period, getStorageKey, getKey) => {
  let coordinatesBuffer = null
  let isDeleteAction = false

  for (let iItemActionPair = 0; iItemActionPair < params.length; iItemActionPair += 2) {
    const action = params[iItemActionPair + 1]
    const quotas = isArray(params[iItemActionPair]) ? params[iItemActionPair] : [params[iItemActionPair]]

    quotas.forEach(quota => {
      const storageKey = isFunction(getStorageKey) ? getStorageKey(quota) : getStorageKey
      const key = isFunction(getKey) && getKey(quota)

      let selectedQuotas = key === false ? rawQuotas : rawQuotas[key]

      if (action !== Actions.DELETE && (quota.startDate > period.endDate || quota.endDate < period.startDate)) {
        selectedQuotas = selectedQuotas.filter(q => q.id !== quota.id)

        if (quota.id < 0) {
          coordinatesBuffer = localStorage.getValue(storageKey, quota.id)
        }

        localStorage.removeValue(storageKey, quota.id)
      } else if (action === Actions.CREATE) {
        selectedQuotas = selectedQuotas.concat([quota])

        if (coordinatesBuffer !== null) {
          localStorage.add(storageKey, { key: quota.id, value: coordinatesBuffer })
          coordinatesBuffer = null
        }
      } else if (action === Actions.UPDATE) {
        selectedQuotas = selectedQuotas.map(q => q.id === quota.id ? quota : q)

        const storedQuota = localStorage.getValue(storageKey, quota.id)

        if (isObject(storedQuota)) {
          localStorage.add(storageKey, { key: quota.id, value: { x: storedQuota.x, y: [storedQuota.y[0], storedQuota.y[0] + quota.total - 1] } })
        }
      } else if (action === Actions.DELETE) {
        selectedQuotas = selectedQuotas.filter(q => q.id !== quota.id)
        isDeleteAction = true

        if (quota.id < 0) {
          coordinatesBuffer = localStorage.getValue(storageKey, quota.id)
        }

        localStorage.removeValue(storageKey, quota.id)
      }

      if (key === false) {
        rawQuotas = selectedQuotas
      } else {
        rawQuotas[key] = selectedQuotas
      }
    })
  }

  return { data: rawQuotas, isDeleteAction }
}

export const getStorageKey = (quota, storageRoot) => {
  return `${storageRoot}.${quota.sector ? quota.sector.id : -1}`
}

export class QuotasMap {
  constructor () {
    this.places = []
    this.quotas = []
    this.quotasNull = []
    this.desideratas = []
  }
}

export class Coordinates {
  constructor ({ x, y }) {
    this.x = x
    this.y = y
  }

  isOnlyVerticalDisplacement (coordinates) {
    return this.x[0] === coordinates.x[0] && this.x[1] === coordinates.x[1] && this.y[0] !== coordinates.y[0] && this.y[1] !== coordinates.y[1]
  }
}
